import React, { useEffect, useState, useCallback } from 'react';

import * as Sentry from '@sentry/browser';
import { debounce } from 'lodash-es';
import { defineMessages } from 'react-intl';

import Avatar from '@atlaskit/avatar';
import { ErrorMessage } from '@atlaskit/form';
import {
  ActionMeta,
  CheckboxSelect,
  OptionType,
  StylesConfig,
} from '@atlaskit/select';

import ModalDialog from 'src/components/modal-dialog-actions';
import { useAnalytics } from 'src/components/settings/analytics';
import {
  modalSelectStyles,
  SearchResultOption,
  OptionAvatar,
} from 'src/components/settings/styles/select';
import { DEBOUNCE_DELAY } from 'src/constants/settings';
import { useFlag } from 'src/hooks/flag';
import { useIntl } from 'src/hooks/intl';
import { useObjectState } from 'src/hooks/use-object-state';
import { useParams } from 'src/router/hooks';
import projectUrls from 'src/urls/projects';
import { httpRequest } from 'src/utils/http-request';

const messages = defineMessages({
  genericErrorHeader: {
    id: 'frontbucket.projectRepositories.page.modal.genericErrorHeader',
    description:
      'Header text for the error flag that appears if repositories are not loaded',
    defaultMessage: 'Failed to fetch repositories',
  },
  genericErrorBody: {
    id: 'frontbucket.projectRepositories.page.modal.genericErrorBody',
    description:
      'Body text for the error flag that appears if repositories are not loaded',
    defaultMessage:
      'Wait a few moments and try again. If this keeps happening, check the status page and/or contact support.',
  },
  cancel: {
    id: 'frontbucket.projectRepositories.page.modal.cancel',
    description: 'Cancel action button text',
    defaultMessage: 'Cancel',
  },
  confirm: {
    id: 'frontbucket.projectRepositories.page.modal.confirm',
    description: 'Confirm action button text',
    defaultMessage: 'Confirm',
  },
  addRepositoryHeader: {
    id: 'frontbucket.projectRepositories.page.modal.addRepositoryHeader',
    description:
      'Message on the top of the modal for adding repositories to a project',
    defaultMessage: 'Add repositories',
  },
  addRepositoryPlaceholder: {
    id: 'frontbucket.projectRepositories.page.modal.addRepositoryPlaceholder',
    description:
      'Input placeholder text for the modal for adding repositories to a project',
    defaultMessage: 'Add one or more repositories by name',
  },
  reposInputMaxedOutError: {
    id: 'frontbucket.projectRepositories.page.modal.reposInputMaxedOutError',
    description:
      'Warning message when the user has input more than the max number of repositories',
    defaultMessage: 'Reduce the number of repositories to no more than {max}.',
  },
});

type RepositoryOptionType = OptionType & { repo: BB.Repository };

export type State = {
  reposToAdd: RepositoryOptionType[];
};

const repoToOption: (repo: BB.Repository) => RepositoryOptionType = repo => ({
  label: repo.name,
  value: repo.uuid,
  repo,
});

const getRepoOptions = (
  workspaceSlug: string,
  projectKey: string,
  options: { search?: string; pagelen?: number }
) =>
  httpRequest<BB.Repository[]>({
    successTransformer: resp => resp.values,
  }).get(
    projectUrls.api.internal.addRepositories(workspaceSlug, projectKey, {
      pagelen: 40,
      sort: 'name',
      ...options,
    })
  );

const getReposByName: (
  input: string,
  workspaceSlug: string,
  projectKey: string,
  setIsLoading: (isLoading: boolean) => void,
  setOptions: (options: RepositoryOptionType[]) => void,
  onGetError: (e: any) => void
) => void = (
  input,
  workspaceSlug,
  projectKey,
  setIsLoading,
  setOptions,
  onGetError
) => {
  setIsLoading(true);
  getRepoOptions(workspaceSlug, projectKey, { search: input })
    .then(repos => {
      setOptions(repos.map(repoToOption));
      setIsLoading(false);
    })
    .catch(e => onGetError(e));
};

const debouncedGetReposByName = debounce(getReposByName, DEBOUNCE_DELAY, {
  trailing: true,
});

const MAX_REPOS_TO_ADD = 20;

/* eslint @typescript-eslint/ban-types: "warn" */
const AddRepositoriesModal: React.FC<{
  isSubmitting: boolean;
  onAddRepos: (repoUuids: string[]) => void;
  onClose: () => void;
}> = ({ isSubmitting, onAddRepos, onClose }) => {
  const { formatMessage } = useIntl();
  const [isLoading, setIsLoading] = useState(false);
  const [inputVal, setInputVal] = useState('');
  const [defaultOptions, setDefaultOptions] = useState<RepositoryOptionType[]>(
    []
  );
  const [options, setOptions] = useState<RepositoryOptionType[]>([]);
  const [state, setState] = useObjectState<State>({
    reposToAdd: [],
  });

  const { publishScreenEvent, publishButtonClickEvent } = useAnalytics();

  const { showErrorFlag } = useFlag({ enableForPdvs: true });
  const { workspaceSlug, projectKey } = useParams<{
    workspaceSlug: string;
    projectKey: string;
  }>();
  if (!workspaceSlug || !projectKey) {
    throw new Error(
      'This component must be rendered on a route that includes both :workspaceSlug and :projectKey'
    );
  }

  const onGetError = useCallback(
    e => {
      setIsLoading(false);
      Sentry.captureException(e);
      showErrorFlag(
        formatMessage(messages.genericErrorBody),
        formatMessage(messages.genericErrorHeader)
      );
    },
    [formatMessage, showErrorFlag]
  );

  useEffect(() => {
    publishScreenEvent();

    setIsLoading(true);
    getRepoOptions(workspaceSlug, projectKey, {})
      .then(repos => {
        const defaults = repos.map(repoToOption);
        setDefaultOptions(defaults);
        setOptions(defaults);
        setIsLoading(false);
      })
      .catch(e => onGetError(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onInputChange = useCallback(
    (input, { action }) => {
      if (action !== 'input-blur' && action !== 'menu-close') {
        setInputVal(input);
        if (input) {
          debouncedGetReposByName(
            input,
            workspaceSlug,
            projectKey,
            setIsLoading,
            setOptions,
            onGetError
          );
        } else {
          setOptions(defaultOptions);
        }
      } else {
        if (!inputVal.trim()) {
          setOptions(defaultOptions);
        }
      }
    },
    [projectKey, workspaceSlug, inputVal, defaultOptions, onGetError]
  );

  const onRepoSelect = (
    selectedOptions: RepositoryOptionType[],
    _: ActionMeta<RepositoryOptionType>
  ) => {
    setState({
      reposToAdd: selectedOptions?.length ? [...selectedOptions] : [],
    });
  };

  const onAddReposClick = () => {
    publishButtonClickEvent('Confirm');
    onAddRepos(state.reposToAdd.map(repoOption => repoOption.repo.uuid));
  };

  const onCloseClick = () => {
    publishButtonClickEvent('Cancel');

    onClose();
  };

  const actions = [
    {
      text: formatMessage(messages.cancel),
      onClick: onCloseClick,
      appearance: 'subtle',
    },
    {
      text: formatMessage(messages.confirm),
      onClick: onAddReposClick,
      appearance: 'primary',
      autoFocus: true,
      isDisabled:
        !state.reposToAdd.length || state.reposToAdd.length > MAX_REPOS_TO_ADD,
      isLoading: isLoading || isSubmitting,
    },
  ];

  const formatOptionLabel = (
    option: RepositoryOptionType,
    { context }: { context: 'menu' | 'value' }
  ) => (
    <SearchResultOption>
      <OptionAvatar>
        <Avatar
          appearance="square"
          name="repositoryAvatar"
          size={context === 'value' ? 'xsmall' : 'small'}
          src={option.repo.links.avatar.href}
          status={option.repo.is_private ? 'locked' : undefined}
        />
      </OptionAvatar>
      <span>{option.label}</span>
    </SearchResultOption>
  );

  return (
    <ModalDialog
      actions={actions as any}
      heading={formatMessage(messages.addRepositoryHeader)}
      width="medium"
      scrollBehavior="outside"
      onClose={onClose}
      autoFocus={false}
    >
      <CheckboxSelect
        isClearable
        autoFocus={false}
        options={options}
        isLoading={isLoading}
        formatOptionLabel={formatOptionLabel}
        placeholder={formatMessage(messages.addRepositoryPlaceholder)}
        inputValue={inputVal}
        onInputChange={onInputChange}
        onChange={onRepoSelect}
        styles={modalSelectStyles as StylesConfig<OptionType, true>}
        components={{
          DropdownIndicator: null,
        }}
      />
      {state.reposToAdd.length > MAX_REPOS_TO_ADD && (
        <ErrorMessage>
          {formatMessage(messages.reposInputMaxedOutError, {
            max: MAX_REPOS_TO_ADD,
          })}
        </ErrorMessage>
      )}
    </ModalDialog>
  );
};

export default AddRepositoriesModal;
